/**
 * API endpoint path constants for the serviceman backend.
 *
 * All endpoints use the `/serviceman/*` prefix. Parameterized endpoints are functions
 * that accept ID arguments and return the full path string.
 *
 * @remarks
 * These paths are relative to the axios base URL (`localhost:9083` in dev, `/api` in prod).
 * The serviceman-api is a Go reverse proxy that routes requests to upstream HV maintenance backends.
 * Most endpoints are explicitly allowlisted in the backend `rest.go` router — no wildcard proxying.
 *
 * Endpoint categories:
 * - **Auth**: PIN-based authentication (request, validate, verify, refresh, logout)
 * - **Maintenance**: CRUD operations, stats, logs, file handling, types
 * - **Apartments & Residents**: Lookup for new maintenance request creation
 * - **Company**: List, detail, receivers, settings, technical details
 * - **Operational Log**: Log entries linked to maintenance requests
 * - **Invitations**: Email invites for maintenance request access
 * - **Meters**: External meter readings (manual type only)
 * - **Personnel**: Personnel management per company
 * - **User Settings**: User profile stored locally in serviceman-api (not proxied)
 */
export const API_ENDPOINTS = {
  // Authentication
  AUTH_REQUEST_PIN: '/serviceman/auth/request-pin',
  AUTH_VALIDATE_PIN: '/serviceman/auth/validate-pin',
  AUTH_VERIFY: '/serviceman/auth/verify',
  /** @deprecated Magic link auth replaced by PIN auth. Only kept for useMagicLinkFallback. */
  AUTH_LINK_QUERY: '/linkquery',
  AUTH_REFRESH_TOKEN: '/serviceman/auth/refresh-token',
  AUTH_LOGOUT: '/serviceman/auth/logout',

  // Maintenance
  MAINTENANCE_ALL: (companyId: string) => `/serviceman/maintenance/all/${companyId}`,
  MAINTENANCE_LIST: '/serviceman/maintenancerequests', // List/filter all maintenance requests
  MAINTENANCE_DETAIL: (id: string) => `/serviceman/maintenancerequest/${id}`,
  MAINTENANCE_CREATE: '/serviceman/createmaintenancerequest', // Create new maintenance request
  MAINTENANCE_UPDATE: (id: string) => `/serviceman/maintenancerequest/${id}`,
  MAINTENANCE_DELETE: (id: string) => `/serviceman/maintenancerequest/${id}`,
  MAINTENANCE_UPDATE_STATUS: '/serviceman/maintenance/updatestatus',
  MAINTENANCE_STATS: '/serviceman/maintenancerequests/stats',
  MAINTENANCE_HISTORY: (id: string) => `/serviceman/history/${id}`,
  MAINTENANCE_LOGS: (id: string) => `/serviceman/maintenancerequest/${id}/logs`,
  MAINTENANCE_FILES: (id: string) => `/serviceman/maintenancerequest/${id}/files`,
  MAINTENANCE_FILE: (id: string, fileId: string) => `/serviceman/maintenancerequest/${id}/file/${fileId}`,
  MAINTENANCE_FILE_UPLOAD: (id: string) => `/serviceman/file/${id}`,
  MAINTENANCE_TYPES: '/serviceman/maintenancetypes/query',

  // Apartments & Residents (for new maintenance request creation)
  APARTMENTS_LIST: (companyId: string) => `/serviceman/apartments/${companyId}`,
  RESIDENTS_LIST: (apartmentGuid: string) => `/serviceman/apartmentspeople/${apartmentGuid}`,

  // Company & Apartment Search
  COMPANIES_FETCH: '/serviceman/companies', // Fetch all accessible companies
  PROPERTY_MANAGERS: '/serviceman/propertymanagers', // Fetch property managers (isännöintitoimistot)
  COMPANY_APARTMENTS: (companyId: string) => `/serviceman/companies/${companyId}/apartments`,
  APARTMENTS_SEARCH: '/serviceman/apartments/search', // Search apartments by address
  APARTMENT_RESIDENTS: (companyId: string, apartmentId: string) => `/serviceman/companies/${companyId}/apartments/${apartmentId}/residents`,
  RESIDENT_VERIFY_BIRTHDATE: (companyId: string, residentId: string) =>
    `/serviceman/companies/${companyId}/residents/${residentId}/verifybirthdate`,

  // Company
  COMPANY_LIST: '/serviceman/company/list',
  COMPANY_DETAIL: (id: string) => `/serviceman/company/${id}`,
  COMPANY_RECEIVERS: (maintenanceRequestId: string) => `/serviceman/receivers/${maintenanceRequestId}`,
  COMPANY_SETTINGS_ACTIVE: (companyId: string) => `/serviceman/companysettings/${companyId}/active`,
  COMPANY_TECHNICAL_DETAILS: (companyId: string) => `/serviceman/companies/${companyId}/technicaldetails`,
  MAINTENANCE_REQUEST_RECIPIENTS: (maintenanceRequestId: string) => `/serviceman/maintenancerequestrecipients/${maintenanceRequestId}`,

  // Operational Log
  OPERATIONAL_LOG: '/serviceman/operational-log',

  // Invitations
  INVITATION: '/serviceman/invitation',
  INVITATION_DELETE: (id: string) => `/serviceman/invitations/delete/${id}`,

  // Meters
  METERS: '/serviceman/meters',
  METER_LIST: (companyId: string) => `/serviceman/meters/${companyId}`,
  METER_READINGS: (companyId: string) => `/serviceman/meterreadings/${companyId}`,
  METER_SUBMIT_READING: (meterId: number) => `/serviceman/meter/readings/${meterId}`,

  // Personnel
  PERSONNEL: '/serviceman/personnel',

  // User Settings (combined profile and preferences)
  USER_SETTINGS_QUERY: '/serviceman/settings/query',
  USER_SETTINGS_UPDATE: '/serviceman/settings',
} as const
